
<template>
  <div class="min-h-screen w-full bg-[#070a13] text-slate-100 relative overflow-hidden antialiased">
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 z-0">
      <div class="absolute inset-0 [background-image:radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:18px_18px]"></div>
      <div class="absolute -top-48 -left-48 h-[36rem] w-[36rem] rounded-full bg-[conic-gradient(at_top_left,_theme(colors.violet.600),theme(colors.fuchsia.500),theme(colors.indigo.600),theme(colors.violet.600))] blur-3xl opacity-30"></div>
      <div class="absolute -bottom-56 -right-56 h-[40rem] w-[40rem] rounded-full bg-[conic-gradient(at_bottom_right,_theme(colors.fuchsia.600),theme(colors.violet.500),theme(colors.blue.600),theme(colors.fuchsia.600))] blur-[110px] opacity-25"></div>
    </div>

    <div class="relative z-10 max-w-5xl mx-auto px-4 py-16 sm:py-24">
      <button
        @click="voltar"
        class="mb-10 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] hover:bg-white/[0.1] px-4 py-2 text-sm font-medium text-white/80 transition-all"
      >
        <span class="material-symbols-outlined text-[18px]">arrow_back</span>
        Voltar
      </button>

      <header class="text-center mb-12">
        <h1 class="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
          Escolha seu plano
        </h1>
        <p class="text-white/60 text-sm max-w-xl mx-auto mt-3">
          Ambos os planos dão
          <strong class="text-violet-300 font-semibold">acesso completo ao Roxinho</strong>
          — o ControlEase — com relatórios, metas, investimentos e tudo mais.
          Troque ou cancele quando quiser.
        </p>

        <div
          v-if="usuarioAutenticado && verificandoStatus"
          class="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-xs text-white/60"
        >
          <span class="material-symbols-outlined text-[16px] animate-spin">progress_activity</span>
          Verificando sua assinatura
        </div>

        <div
          v-else-if="usuarioAutenticado && assinaturaAtiva"
          class="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-2 text-xs font-medium text-emerald-300"
        >
          <span class="material-symbols-outlined text-[17px]">verified</span>
          Você já possui uma assinatura ativa
        </div>

        <div
          v-else-if="usuarioAutenticado && podeUsarTrial"
          class="mt-6 inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-500/10 px-4 py-2 text-xs font-medium text-violet-200"
        >
          <span class="material-symbols-outlined text-[17px]">redeem</span>
          Seu primeiro dia gratuito está disponível
        </div>

        <div
          v-else-if="usuarioAutenticado && trialUtilizado"
          class="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-500/10 px-4 py-2 text-xs font-medium text-amber-200"
        >
          <span class="material-symbols-outlined text-[17px]">info</span>
          Seu período gratuito já foi utilizado
        </div>

        <div
          v-if="erroStatus"
          class="mt-5 text-sm text-amber-200/80"
        >
          {{ erroStatus }}
          <button
            @click="atualizarElegibilidade"
            class="ml-2 text-violet-300 hover:text-violet-200 underline"
          >
            Tentar novamente
          </button>
        </div>
      </header>

      <div
        v-if="billingStore.loadingPlans"
        class="text-center text-white/60 text-sm py-16"
      >
        Carregando planos…
      </div>

      <div
        v-else-if="!billingStore.plans?.length"
        class="text-center text-white/60 text-sm py-16"
      >
        Nenhum plano disponível no momento.
      </div>

      <div v-else class="grid gap-8 sm:grid-cols-2">
        <div
          v-for="plan in billingStore.plans"
          :key="plan.id"
          class="relative rounded-[28px] border border-white/10 bg-white/[0.06] shadow-[0_30px_120px_-30px_rgba(0,0,0,.7)] backdrop-blur-xl p-8 flex flex-col"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-xl font-semibold tracking-tight text-white flex items-center gap-2">
                {{ plan.name }}
                <span
                  v-if="planRecommended(plan)"
                  class="inline-block rounded-full bg-violet-600/20 text-violet-200 text-[10px] font-semibold px-2 py-0.5 leading-none border border-violet-400/30"
                >
                  recomendado
                </span>
              </h2>

              <p class="text-white/60 text-sm mt-2 leading-relaxed">
                Acesso completo ao
                <span class="text-violet-300 font-medium">Roxinho</span>
                com todas as ferramentas premium.
              </p>
            </div>

            <div class="rounded-2xl bg-white/[0.07] ring-1 ring-white/15 px-3 py-2 text-right shrink-0">
              <div class="text-[10px] text-white/50 uppercase tracking-wide">
                {{ podeUsarTrial ? 'primeiro dia' : 'valor do plano' }}
              </div>

              <div class="text-sm font-semibold text-white">
                <template v-if="assinaturaAtiva">
                  Plano ativo
                </template>
                <template v-else-if="podeUsarTrial">
                  1 dia grátis
                </template>
                <template v-else>
                  {{ centsToMoney(plan.amount) }}
                </template>
              </div>

              <div class="mt-1 text-[11px] text-white/60">
                <template v-if="podeUsarTrial">
                  {{ afterPrice(plan) }}
                </template>
                <template v-else>
                  {{ plan.intervalValue === 'year' ? 'por ano' : 'por mês' }}
                </template>
              </div>
            </div>
          </div>

          <ul class="mt-6 space-y-3 text-sm text-white/80 flex-1">
            <li class="flex items-start gap-2">
              <span class="material-symbols-outlined text-[18px] text-violet-300 shrink-0">
                check_circle
              </span>
              <span class="leading-relaxed">
                Controle financeiro completo
              </span>
            </li>

            <li class="flex items-start gap-2">
              <span class="material-symbols-outlined text-[18px] text-violet-300 shrink-0">
                check_circle
              </span>
              <span class="leading-relaxed">
                Metas, investimentos e tarefas ilimitadas
              </span>
            </li>

            <li class="flex items-start gap-2">
              <span class="material-symbols-outlined text-[18px] text-violet-300 shrink-0">
                check_circle
              </span>
              <span class="leading-relaxed">
                Cancelamento simples direto no app
              </span>
            </li>

            <li
              v-if="podeUsarTrial"
              class="flex items-start gap-2"
            >
              <span class="material-symbols-outlined text-[18px] text-violet-300 shrink-0">
                redeem
              </span>
              <span class="leading-relaxed">
                1 dia gratuito exclusivo para seu primeiro teste
              </span>
            </li>
          </ul>

          <div class="mt-8 grid gap-3">
            <button
              @click="assinar(plan.id)"
              class="group relative w-full rounded-2xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-indigo-600 px-4 py-3 text-sm font-semibold shadow-[0_12px_40px_-8px_rgba(124,58,237,.55)] active:scale-[.99] disabled:opacity-70 flex flex-col text-left transition-all hover:brightness-110"
              :disabled="billingStore.loadingCheckout || verificandoStatus"
            >
              <div class="flex items-center justify-between w-full">
                <span class="text-white font-semibold tracking-tight flex items-center gap-2">
                  <span
                    v-if="billingStore.loadingCheckout || verificandoStatus"
                    class="material-symbols-outlined text-[18px] animate-spin"
                  >
                    progress_activity
                  </span>

                  <span v-if="assinaturaAtiva">
                    Gerenciar assinatura
                  </span>
                  <span v-else-if="podeUsarTrial">
                    Começar 1 dia grátis
                  </span>
                  <span v-else-if="!usuarioAutenticado">
                    Entrar e escolher plano
                  </span>
                  <span v-else>
                    Assinar agora
                  </span>
                </span>

                <span class="material-symbols-outlined text-[20px] text-white/80">
                  arrow_forward
                </span>
              </div>

              <div class="mt-1 text-white/70 text-[11px]">
                <template v-if="assinaturaAtiva">
                  Consulte ou gerencie sua assinatura atual
                </template>
                <template v-else-if="podeUsarTrial">
                  Acesso completo hoje grátis • depois {{ priceInterval(plan) }}
                </template>
                <template v-else-if="trialUtilizado">
                  Sem novo teste gratuito • {{ priceInterval(plan) }}
                </template>
                <template v-else-if="!usuarioAutenticado">
                  Teste de 1 dia sujeito à elegibilidade • {{ priceInterval(plan) }}
                </template>
                <template v-else>
                  Acesso completo • {{ priceInterval(plan) }}
                </template>
              </div>

              <span class="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/10"></span>
            </button>

            <p
              v-if="billingStore.error"
              class="text-[12px] text-rose-400 text-center"
            >
              {{ billingStore.error }}
            </p>
          </div>

          <div class="mt-6 flex items-center justify-between text-[11px] text-white/40 gap-2">
            <div class="flex items-center gap-1.5">
              <span class="material-symbols-outlined text-[14px]">
                lock
              </span>
              <span>Checkout seguro via Stripe</span>
            </div>
            <div>Cancelar quando quiser</div>
          </div>

          <div class="absolute inset-0 pointer-events-none rounded-[28px] ring-1 ring-white/10 shadow-[0_30px_120px_-30px_rgba(124,58,237,.4)]"></div>
        </div>
      </div>

      <div class="text-center mt-16 text-[11px] text-white/40">
        Ao continuar você concorda com a cobrança recorrente e nossos termos de uso.
        O teste gratuito de 1 dia está disponível somente uma vez por usuário elegível.
      </div>
    </div>

    <LoginModal
      :show="showLogin"
      @close="showLogin = false"
      @switch-view="onSwitchAuth"
      @login-success="onLoginSuccess"
    />

    <RegisterModal
      :show="activeAuthView === 'register'"
      @close="closeAuth"
      @switch-view="onSwitchAuth"
      @go-to-address="onGoToAddress"
    />

    <CompleteSignupModal
      :show="activeAuthView === 'completeSignup'"
      :google-email="modalPayload.email"
      :google-nome="modalPayload.nome"
      @close="closeAuth"
      @go-to-address="onGoToAddress"
    />

    <RegisterAddressModal
      :show="activeAuthView === 'registerAddress'"
      :user-id="modalPayload.userId"
      @close="closeAuth"
      @registration-complete="onRegistrationComplete"
      @switch-view="onSwitchAuth"
    />

    <ForgotPasswordModal
      :show="activeAuthView === 'forgotPassword'"
      @close="closeAuth"
      @switch-view="onSwitchAuth"
    />

    <ResetPasswordModal
      :show="activeAuthView === 'resetPassword'"
      :email="modalPayload.email"
      @close="closeAuth"
      @password-reset-success="onPasswordResetSuccess"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useBillingStore } from '@/stores/billing'
import { useAuthStore } from '@/stores/auth'
import LoginModal from '@/components/auth/LoginPage.vue'
import RegisterModal from '@/components/auth/RegisterPage.vue'
import CompleteSignupModal from '@/components/auth/CompleteSignup.vue'
import RegisterAddressModal from '@/components/auth/RegisterAddress.vue'
import ForgotPasswordModal from '@/components/auth/ForgotPassword.vue'
import ResetPasswordModal from '@/components/auth/ResetPassword.vue'

const billingStore = useBillingStore()
const authStore = useAuthStore()
const router = useRouter()

const showLogin = ref(false)
const activeAuthView = ref(null)
const modalPayload = ref({})
const authEmail = ref('')
const pendingPlanId = ref(null)

const usuarioAutenticado = ref(false)
const verificandoStatus = ref(false)
const statusAssinatura = ref(null)
const erroStatus = ref('')

const getToken = () => {
  return authStore.token || localStorage.getItem('token')
}

const podeUsarTrial = computed(() => {
  return usuarioAutenticado.value &&
    statusAssinatura.value?.canTrial === true &&
    statusAssinatura.value?.hasActive !== true
})

const trialUtilizado = computed(() => {
  return usuarioAutenticado.value &&
    statusAssinatura.value?.canTrial === false
})

const assinaturaAtiva = computed(() => {
  return usuarioAutenticado.value &&
    statusAssinatura.value?.hasActive === true
})

const centsToMoney = (cents) => {
  if (cents == null) return ''
  const value = (cents / 100).toFixed(2).replace('.', ',')
  return `U$ ${value}`
}

const priceInterval = (plan) => {
  return `${centsToMoney(plan?.amount)} / ${plan?.intervalValue === 'year' ? 'ano' : 'mês'}`
}

const afterPrice = (plan) => {
  return `Depois ${priceInterval(plan)}`
}

const planRecommended = (plan) => {
  return plan?.intervalValue === 'year'
}

const atualizarElegibilidade = async () => {
  const token = getToken()

  usuarioAutenticado.value = Boolean(token)
  statusAssinatura.value = null
  erroStatus.value = ''

  if (!token) {
    verificandoStatus.value = false
    return
  }

  verificandoStatus.value = true

  try {
    const response = await axios.get(
      'https://backend.controlease.com.br/api/billing/status',
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000
      }
    )

    const dados = response.data || {}

    statusAssinatura.value = {
      hasActive: dados.hasActive === true,
      canTrial: typeof dados.canTrial === 'boolean'
        ? dados.canTrial
        : null,
      plano: dados.plano,
      status: dados.statusAssinatura
    }

    if (typeof dados.canTrial !== 'boolean') {
      erroStatus.value = 'Não foi possível confirmar seu benefício de teste gratuito.'
    }
  } catch (error) {
    statusAssinatura.value = null

    if (
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      erroStatus.value = 'Sua sessão expirou. Faça login novamente.'
    } else {
      erroStatus.value = 'Não foi possível consultar sua assinatura no momento.'
    }
  } finally {
    verificandoStatus.value = false
  }
}

const assinar = async (planId) => {
  const token = getToken()

  if (!token) {
    pendingPlanId.value = planId
    openAuth('login')
    return
  }

  if (assinaturaAtiva.value) {
    await router.push({ name: 'MinhaAssinatura' })
    return
  }

  if (verificandoStatus.value || billingStore.loadingCheckout) {
    return
  }

  await billingStore.startCheckout(planId)
}

const openAuth = (view, payload = {}) => {
  modalPayload.value = payload

  if (view === 'login') {
    activeAuthView.value = null
    showLogin.value = true
  } else {
    showLogin.value = false
    activeAuthView.value = view
  }

  if (payload?.email) {
    authEmail.value = payload.email
  }
}

const onSwitchAuth = (viewName, payload = {}) => {
  openAuth(viewName, payload)
}

const onGoToAddress = (data) => {
  modalPayload.value = {
    userId: data.userId
  }

  openAuth('registerAddress', modalPayload.value)
}

const onRegistrationComplete = () => {
  openAuth('login')
}

const onPasswordResetSuccess = () => {
  openAuth('login', {
    email: authEmail.value
  })
}

const onLoginSuccess = async () => {
  showLogin.value = false

  await atualizarElegibilidade()

  if (pendingPlanId.value) {
    const planId = pendingPlanId.value
    pendingPlanId.value = null

    if (assinaturaAtiva.value) {
      await router.push({ name: 'MinhaAssinatura' })
      return
    }

    await billingStore.startCheckout(planId)
    return
  }

  if (assinaturaAtiva.value) {
    await router.push('/user/dashboard')
  }
}

const closeAuth = () => {
  activeAuthView.value = null
  showLogin.value = false
}

const voltar = async () => {
  if (getToken()) {
    await router.push({ name: 'MinhaAssinatura' })
  } else {
    await router.push({ name: 'Home' })
  }
}

onMounted(async () => {
  await Promise.all([
    billingStore.fetchPlans(),
    atualizarElegibilidade()
  ])
})
</script>

<style scoped></style>
